<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
        btnTitle="Add New"
        :showBtn="false"
        title="Reward Point Statement"
      />
    </div>

    <div class="my-2 px-2">
      <DateQuerySetter
        btnTitle="Go"
        @onClickGo="onClickGo"
      >

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">
            Customer <span class="text-danger">*</span>
          </label>
          <AsyncSelect
            placeholder="Party Name, ID, Mobile No"
            :api-service="fetchContactProfiles"
            v-model="contactProfile"
            :additional-query="{type: 'customer'}"
            :format-label="formatPatientLabel"
          />
        </div>
      </DateQuerySetter>
    </div>
    <div class="col-12 px-2 mt-2" v-if="hasRewardLog">
      <div class="d-flex justify-content-end px-2 mb-1">
        <button
          @click="goToPrint"
          class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect mx-1"
        >
          Print
        </button>
      </div>
    </div>
    <div class="col-12 px-2 mt-2">
      <ListTable 
        :openingBalance="openingBalance"
        :closingBalance="closingBalance"
        :tableItems="rewardLogList ? rewardLogList.data : []"
        :currentPage="rewardLogList ? rewardLogList.current_page : 1"
        :lastPage="rewardLogList ? rewardLogList.last_page : 1"
       />
    </div>
    <div class="px-2"><Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/></div>
  </div>
</template>

<script setup>
import DateQuerySetter from '@/components/atom/DateQuerySetter';
import { ref, inject, onMounted, computed  } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import TitleButton from '@/components/atom/TitleButton';
import Pagination from '@/components/atom/Pagination';
import handleContact from "@/services/modules/contact";
import ListTable from '@/components/atom/super-shop/reward-point/StatementListTable.vue';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";
import handleInventory from "@/services/modules/inventory";
import handleCompany from "@/services/modules/company";
import pdfPrinter from '@/services/utils/pdf/rewardPointStatementPdf';

const router = useRouter()
const store = useStore()
let offset = ref(20)
let contactProfile = ref(null)
const route = useRoute()
const openingBalance = ref(0);
const closingBalance = ref(0);
const rewardLogList = ref({});
const companyInfo = ref(null);

const { fetchContactProfiles } = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();
const { fetchRewardStatement } = handleInventory();
const { fetchCompanyInfo } = handleCompany();
const { exportToPDF } = pdfPrinter();

const showError = inject('showError');

const hasRewardLog = computed(() => {
  if (! rewardLogList.value) return false;
  if (! rewardLogList.value.data) return false;
  return rewardLogList.value.data.length > 0;
})
const companyId = computed(() => {
  return route.params.companyId
})
const startDate = computed(() => {
  return route.query.start
})
const endDate = computed(() => {
  return route.query.end
})
const page = computed(() => {
  return route.query.page
})

const getQuery = () => {
  let query = '?company_id=' + companyId.value + '&status=approved,active';
  if(startDate.value) query += '&start_date='+startDate.value;
  if(endDate.value) query += '&end_date='+endDate.value;
  query += '&offset='+ offset.value;
  query += '&page='+ page.value;

  return query
}

const onClickGo = async() => {
  if(! contactProfile.value) {
    showError('Please select customer');
    return;
  }

  const query = getQuery();

  try{
    const res = await fetchRewardStatement(contactProfile.value.id, query);
    if(! res.status) {
      rewardLogList.value = [];
      setPagination({data: []});
      return;
    }
    openingBalance.value = res.data.opening_balance;
    closingBalance.value = res.data.closing_balance;
    rewardLogList.value = res.data.reward_point_log;
    setPagination(res.data.reward_point_log);
  } catch(err){
    showError('Something went wrong');
  }
};

const goToPrint = async() => {
  if(! contactProfile.value) {
    showError('Please select customer');
    return;
  }

  let query = getQuery();
  query += '&is_export=1';

  try{
    const res = await fetchRewardStatement(contactProfile.value.id, query);
    if(! res.status) {
      showError(res.message);
      return;
    }
    const statementData = {
      party_name: contactProfile.value.name,
      report_period: startDate.value + ' to ' + endDate.value,
      opening_balance: res.data.opening_balance,
      closing_balance: res.data.closing_balance,
      reward_point_log: res.data.reward_point_log,
    };
    exportToPDF(companyInfo.value, statementData);
  } catch(err){
    showError('Something went wrong');
  }
};

function setPagination(data) {
  if(!data.data.length) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
};

function onPageChange(page) {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  router.push({path: route.path, query: routeQuery})
  onClickGo()
};

const getCompany = async () => {
  const res = await fetchCompanyInfo(companyId.value);
  companyInfo.value = res.data;
};

onMounted(() => {
  getCompany();
});
</script>
